import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useId, useState } from "react";
import { ArrowLeft, UserPlus, UserMinus } from "lucide-react";
import {
  useCollaborators,
  useRenameApp,
  useAddCollaborator,
  useUpdateCollaborator,
  useRemoveCollaborator,
  useTransferApp,
  useCreateAppTransfer,
  useAppTransfers,
  useCancelAppTransfer,
} from "@/features/apps/hooks/useApps";
import type {
  AssignableRole,
  CollaboratorMap,
  AppTransferWarnings,
} from "@/features/apps/api/apps.api";
import { accessFrom, type AppAccess } from "@/features/apps/lib/capabilities";
import { DeleteAppDialog } from "@/features/apps/components/DeleteAppDialog";
import { Alert } from "@/components/ui/Alert";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { ApiFieldErrors } from "@/components/ui/ApiFieldErrors";
import { apiFieldDescribedBy, apiFieldHasError } from "@/components/ui/api-field-errors";
import { PageLoader } from "@/components/ui/PageLoader";
import { formatDate } from "@/lib/formatters";

export const Route = createFileRoute("/_authenticated/apps/$appName_/settings")({
  component: AppSettingsPage,
});

const inputClass = `
  h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading
  transition-colors duration-150
  placeholder:text-muted
  focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo
  disabled:cursor-not-allowed disabled:opacity-50
  aria-disabled:cursor-not-allowed aria-disabled:opacity-50
  hover:border-border-light
`;

const selectBaseClass = `
  shrink-0 rounded-lg border border-border bg-surface px-3 text-heading
  transition-colors duration-150
  focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo
  disabled:cursor-not-allowed disabled:opacity-50
  aria-disabled:cursor-not-allowed aria-disabled:opacity-50
  hover:border-border-light
`;

const inviteSelectClass = `${selectBaseClass} h-11 text-sm`;
const rowSelectClass = `${selectBaseClass} h-8 py-0 text-xs`;

const secondaryButtonClass =
  "rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed";

const primarySubmitClass =
  "inline-flex shrink-0 items-center gap-2 rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed";

const ASSIGNABLE_ROLES: AssignableRole[] = ["Admin", "Developer", "Viewer"];

function AppSettingsPage() {
  const { appName } = Route.useParams();
  const { t } = useTranslation();
  const collaboratorsQuery = useCollaborators(appName);

  const collaborators = collaboratorsQuery.data?.collaborators ?? {};
  const access = accessFrom(collaboratorsQuery.data?.capabilities, collaborators);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex items-center gap-4">
        <Link
          to="/apps/$appName"
          params={{ appName }}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
        >
          <ArrowLeft size={18} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-heading">{t("apps.settings")}</h1>
          <p className="mt-0.5 text-sm text-muted">{appName}</p>
        </div>
      </div>

      {collaboratorsQuery.isLoading && <PageLoader />}
      <ApiErrorBanner error={collaboratorsQuery.error} className="mt-8" />

      {!collaboratorsQuery.isLoading && !collaboratorsQuery.error && (
        <div className="mt-8 space-y-6">
          <RenameSection key={appName} appName={appName} canRename={access.can("app.manage")} />
          <CollaboratorsSection appName={appName} collaborators={collaborators} access={access} />
          <DangerZoneSection appName={appName} access={access} />
        </div>
      )}
    </div>
  );
}

export function RenameSection({ appName, canRename }: { appName: string; canRename: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const renameApp = useRenameApp();
  const [name, setName] = useState(appName);
  const fieldErrorPrefix = useId();
  const nameInvalid = apiFieldHasError(renameApp.error, "name");

  const trimmed = name.trim();
  const canSave = canRename && trimmed !== "" && trimmed !== appName;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (renameApp.isPending) return;
    if (!canSave) return;

    renameApp.mutate(
      { appName, newName: trimmed },
      {
        onSuccess: ({ app }) => {
          navigate({
            to: "/apps/$appName/settings",
            params: { appName: app.name },
            replace: true,
          });
        },
      },
    );
  };

  return (
    <section className="rounded-xl border border-border bg-surface p-5">
      <h2 className="text-base font-semibold text-heading">{t("apps.renameApp")}</h2>
      {!canRename && <p className="mt-1 text-xs text-muted">{t("apps.ownerOnly")}</p>}
      <form onSubmit={handleSubmit} className="mt-4 flex items-center gap-3">
        <input
          id="app-rename"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (renameApp.error) renameApp.reset();
          }}
          aria-label={t("apps.appNameLabel")}
          disabled={!canRename}
          readOnly={canRename && renameApp.isPending}
          aria-disabled={!canRename || renameApp.isPending || undefined}
          aria-invalid={nameInvalid ? true : undefined}
          aria-describedby={apiFieldDescribedBy(renameApp.error, "name", fieldErrorPrefix)}
          className={inputClass}
        />
        <button
          type="submit"
          disabled={!renameApp.isPending && !canSave}
          aria-disabled={renameApp.isPending || undefined}
          aria-busy={renameApp.isPending || undefined}
          data-autofocus={renameApp.isPending ? true : undefined}
          className={primarySubmitClass}
        >
          {renameApp.isPending ? t("common.saving") : t("common.save")}
        </button>
      </form>
      <ApiFieldErrors
        error={renameApp.error}
        fieldLabels={{ name: t("apps.appNameLabel") }}
        idPrefix={fieldErrorPrefix}
        className="mt-3"
      />
    </section>
  );
}

const ROLE_BADGE_CLASSES: Record<string, string> = {
  Owner: "bg-indigo/10 text-indigo",
  Admin: "bg-cyan/10 text-cyan",
  Developer: "bg-elevated text-muted",
  Viewer: "bg-elevated text-muted",
};

function roleLabelKey(
  permission: string,
):
  | "apps.permissionOwner"
  | "apps.permissionAdmin"
  | "apps.permissionDeveloper"
  | "apps.permissionViewer"
  | "apps.permissionCollaborator" {
  switch (permission) {
    case "Owner":
      return "apps.permissionOwner";
    case "Admin":
      return "apps.permissionAdmin";
    case "Developer":
      return "apps.permissionDeveloper";
    case "Viewer":
      return "apps.permissionViewer";
    default:
      return "apps.permissionCollaborator";
  }
}

function RoleBadge({ permission }: { permission: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
        ROLE_BADGE_CLASSES[permission] ?? "bg-elevated text-muted"
      }`}
    >
      {t(roleLabelKey(permission))}
    </span>
  );
}

interface CollaboratorsSectionProps {
  appName: string;
  collaborators: CollaboratorMap;
  access: AppAccess;
}

export function CollaboratorsSection({
  appName,
  collaborators,
  access,
}: CollaboratorsSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addCollaborator = useAddCollaborator(appName);
  const updateCollaborator = useUpdateCollaborator(appName);
  const removeCollaborator = useRemoveCollaborator(appName);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AssignableRole>("Developer");
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);

  const entries = Object.entries(collaborators);
  const canManage = access.can("collaborators.manage");
  const canPickRoles = canManage && access.serverCapabilities;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (addCollaborator.isPending) return;
    const trimmed = email.trim();
    if (!trimmed) return;

    addCollaborator.mutate(
      { email: trimmed, permission: access.serverCapabilities ? role : undefined },
      { onSuccess: () => setEmail("") },
    );
  };

  return (
    <section className="rounded-xl border border-border bg-surface p-5">
      <h2 className="text-base font-semibold text-heading">{t("apps.collaborators")}</h2>

      {entries.length === 0 && (
        <p className="mt-4 text-sm text-muted">{t("apps.noCollaborators")}</p>
      )}

      {entries.length > 0 && (
        <ul className="mt-4 divide-y divide-border">
          {entries.map(([collaboratorEmail, props]) => {
            const canRemove = props.isCurrentAccount
              ? props.permission !== "Owner"
              : canManage && props.permission !== "Owner";
            const canEditRole =
              canPickRoles && props.permission !== "Owner" && !props.isCurrentAccount;
            return (
              <li key={collaboratorEmail} className="flex items-center justify-between gap-3 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="min-w-0 truncate text-sm text-heading">{collaboratorEmail}</span>
                  {props.isCurrentAccount && (
                    <span className="shrink-0 text-xs text-muted">({t("apps.you")})</span>
                  )}
                  <RoleBadge permission={props.permission} />
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {canEditRole && (
                    <select
                      value={
                        ASSIGNABLE_ROLES.includes(props.permission as AssignableRole)
                          ? props.permission
                          : "Developer"
                      }
                      onChange={(e) =>
                        updateCollaborator.mutate({
                          email: collaboratorEmail,
                          permission: e.target.value as AssignableRole,
                        })
                      }
                      disabled={updateCollaborator.isPending}
                      aria-label={t("apps.changeRoleFor", { email: collaboratorEmail })}
                      className={rowSelectClass}
                    >
                      {ASSIGNABLE_ROLES.map((assignable) => (
                        <option key={assignable} value={assignable}>
                          {t(roleLabelKey(assignable))}
                        </option>
                      ))}
                    </select>
                  )}
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => setRemoveTarget(collaboratorEmail)}
                      disabled={removeCollaborator.isPending}
                      title={t("apps.removeCollaborator")}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                    >
                      <UserMinus size={15} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {canManage && (
        <>
          <form onSubmit={handleAdd} className="mt-4 flex flex-wrap items-center gap-3">
            <input
              id="collaborator-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("apps.collaboratorEmailPlaceholder")}
              aria-label={t("apps.addCollaborator")}
              readOnly={addCollaborator.isPending}
              aria-disabled={addCollaborator.isPending || undefined}
              className={`${inputClass} min-w-48 flex-1 basis-56`}
            />
            {canPickRoles && (
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as AssignableRole)}
                aria-disabled={addCollaborator.isPending || undefined}
                aria-label={t("apps.role")}
                className={inviteSelectClass}
              >
                {ASSIGNABLE_ROLES.map((assignable) => (
                  <option key={assignable} value={assignable}>
                    {t(roleLabelKey(assignable))}
                  </option>
                ))}
              </select>
            )}
            <button
              type="submit"
              disabled={!addCollaborator.isPending && !email.trim()}
              aria-disabled={addCollaborator.isPending || undefined}
              aria-busy={addCollaborator.isPending || undefined}
              data-autofocus={addCollaborator.isPending ? true : undefined}
              className={primarySubmitClass}
            >
              <UserPlus size={15} />
              {addCollaborator.isPending ? t("common.saving") : t("apps.addCollaborator")}
            </button>
          </form>
          {canPickRoles && <p className="mt-2 text-xs text-muted">{t("apps.roleHelp")}</p>}
        </>
      )}

      {(addCollaborator.error || updateCollaborator.error || removeCollaborator.error) && (
        <p role="alert" className="mt-3 text-xs text-danger">
          {addCollaborator.error?.error ??
            updateCollaborator.error?.error ??
            removeCollaborator.error?.error ??
            t("common.error")}
        </p>
      )}

      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (!removeTarget) return;
          const isSelf = collaborators[removeTarget]?.isCurrentAccount;
          removeCollaborator.mutate(removeTarget, {
            onSuccess: () => {
              if (isSelf) navigate({ to: "/apps" });
            },
          });
        }}
        title={t("apps.removeCollaborator")}
        description={t("apps.removeCollaboratorConfirm", { email: removeTarget ?? "" })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        isPending={removeCollaborator.isPending}
      />
    </section>
  );
}

export function DangerZoneSection({ appName, access }: { appName: string; access: AppAccess }) {
  const { t } = useTranslation();
  const transferApp = useTransferApp();
  const createAppTransfer = useCreateAppTransfer();
  const cancelTransfer = useCancelAppTransfer();
  const transfersQuery = useAppTransfers();
  const [transferEmail, setTransferEmail] = useState("");
  const [transferScope, setTransferScope] = useState<"same" | "cross">("same");
  const [transferConfirmOpen, setTransferConfirmOpen] = useState(false);
  const [createWarnings, setCreateWarnings] = useState<AppTransferWarnings | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  const canTransfer = access.can("app.transfer");
  const canDelete = access.can("app.delete");

  if (!canTransfer && !canDelete) return null;

  const trimmedEmail = transferEmail.trim();
  const isCross = transferScope === "cross";
  const transferPending = transferApp.isPending || createAppTransfer.isPending;
  const transferError = isCross ? createAppTransfer.error : transferApp.error;
  const outboundForApp =
    transfersQuery.data?.outbound.filter((transfer) => transfer.appName === appName) ?? [];

  return (
    <section className="rounded-xl border border-danger/30 bg-surface p-5">
      <h2 className="text-base font-semibold text-danger">{t("apps.dangerZone")}</h2>

      {canTransfer && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-heading">{t("apps.transferOwnership")}</h3>
          <p className="mt-1 text-xs text-muted">
            {isCross ? t("apps.transferCrossWarning") : t("apps.transferWarning")}
          </p>

          {outboundForApp.length > 0 && (
            <div className="mt-3 rounded-lg border border-warning/30 bg-warning/10 p-3">
              <p className="text-xs font-medium text-heading">
                {t("apps.transfers.pendingOutbound")}
              </p>
              <ul className="mt-2 space-y-2">
                {outboundForApp.map((transfer) => (
                  <li
                    key={transfer.id}
                    className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted"
                  >
                    <span>
                      {t("apps.transfers.to", { email: transfer.toEmail ?? "—" })}
                      {" · "}
                      {t("apps.transfers.expires", {
                        date: formatDate(transfer.expiresAt),
                      })}
                    </span>
                    <button
                      type="button"
                      className={secondaryButtonClass}
                      aria-disabled={cancelTransfer.isPending || undefined}
                      onClick={() => {
                        if (cancelTransfer.isPending) return;
                        setCancelTargetId(transfer.id);
                      }}
                    >
                      {cancelTransfer.isPending && cancelTargetId === transfer.id
                        ? t("apps.transfers.cancelling")
                        : t("apps.transfers.cancel")}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <fieldset className="mt-3 space-y-2">
            <legend className="sr-only">{t("apps.transferScopeLegend")}</legend>
            <label className="flex items-start gap-2 text-xs text-heading">
              <input
                type="radio"
                name="transfer-scope"
                value="same"
                checked={transferScope === "same"}
                onChange={() => {
                  if (transferPending) return;
                  setTransferScope("same");
                  setCreateWarnings(null);
                  createAppTransfer.reset();
                  transferApp.reset();
                }}
                aria-disabled={transferPending || undefined}
                className="mt-0.5 aria-disabled:opacity-50"
              />
              <span>{t("apps.transferScopeSame")}</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-heading">
              <input
                type="radio"
                name="transfer-scope"
                value="cross"
                checked={transferScope === "cross"}
                onChange={() => {
                  if (transferPending) return;
                  setTransferScope("cross");
                  setCreateWarnings(null);
                  createAppTransfer.reset();
                  transferApp.reset();
                }}
                aria-disabled={transferPending || undefined}
                className="mt-0.5 aria-disabled:opacity-50"
              />
              <span>{t("apps.transferScopeCross")}</span>
            </label>
          </fieldset>

          {isCross && (
            <Alert variant="warning" className="mt-3">
              {t("apps.transferCrossCollaboratorsLeave")}
            </Alert>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (transferPending) return;
              if (trimmedEmail) setTransferConfirmOpen(true);
            }}
            className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <input
              id="transfer-email"
              type="email"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              placeholder={t("apps.collaboratorEmailPlaceholder")}
              aria-label={t("apps.newOwnerEmailLabel")}
              readOnly={transferPending}
              aria-disabled={transferPending || undefined}
              className={inputClass}
            />
            <button
              type="submit"
              disabled={!transferPending && !trimmedEmail}
              aria-disabled={transferPending || undefined}
              aria-busy={transferPending || undefined}
              data-autofocus={transferPending ? true : undefined}
              className={`w-fit shrink-0 ${secondaryButtonClass}`}
            >
              {transferPending
                ? t("apps.transferring")
                : isCross
                  ? t("apps.transferRequest")
                  : t("apps.transferOwnership")}
            </button>
          </form>

          {createWarnings && (createWarnings.quotaExceeded || createWarnings.nameConflict) && (
            <div className="mt-3 space-y-2">
              {createWarnings.quotaExceeded && (
                <Alert variant="warning">{t("apps.transfers.warningQuota")}</Alert>
              )}
              {createWarnings.nameConflict && (
                <Alert variant="warning">{t("apps.transfers.warningName")}</Alert>
              )}
            </div>
          )}

          {transferError && (
            <p role="alert" className="mt-3 text-xs text-danger">
              {transferError.error ?? t("common.error")}
            </p>
          )}
        </div>
      )}

      {canDelete && (
        <div
          className={`mt-6 flex items-center justify-between pt-4 ${canTransfer ? "border-t border-border" : ""}`}
        >
          <div>
            <h3 className="text-sm font-semibold text-heading">{t("apps.deleteApp")}</h3>
            <p className="mt-1 text-xs text-muted">{t("apps.deleteAppWarning")}</p>
          </div>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="shrink-0 rounded-lg border border-danger/30 bg-surface px-4 py-2.5 text-sm font-medium text-danger transition-colors hover:bg-danger/10"
          >
            {t("apps.deleteApp")}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={transferConfirmOpen}
        onClose={() => setTransferConfirmOpen(false)}
        onConfirm={() => {
          if (isCross) {
            createAppTransfer.mutate(
              { appName, email: trimmedEmail },
              {
                onSuccess: (result) => {
                  setTransferEmail("");
                  setCreateWarnings(result.warnings);
                  setTransferConfirmOpen(false);
                },
              },
            );
            return;
          }
          transferApp.mutate(
            { appName, email: trimmedEmail },
            {
              onSuccess: () => {
                setTransferEmail("");
                setCreateWarnings(null);
                setTransferConfirmOpen(false);
              },
            },
          );
        }}
        title={t("apps.transferOwnership")}
        description={
          isCross
            ? t("apps.transferCrossConfirm", { appName, email: trimmedEmail })
            : t("apps.transferConfirm", { appName, email: trimmedEmail })
        }
        confirmLabel={t("common.confirm")}
        cancelLabel={t("common.cancel")}
        destructive
        closeOnConfirm={false}
        isPending={transferPending}
        error={transferError?.error}
      />

      <ConfirmDialog
        open={cancelTargetId !== null}
        onClose={() => setCancelTargetId(null)}
        onConfirm={() => {
          if (!cancelTargetId) return;
          cancelTransfer.mutate(cancelTargetId, {
            onSuccess: () => setCancelTargetId(null),
          });
        }}
        title={t("apps.transfers.cancelTitle")}
        description={t("apps.transfers.cancelConfirm", { appName })}
        confirmLabel={t("apps.transfers.cancel")}
        cancelLabel={t("common.cancel")}
        destructive
        closeOnConfirm={false}
        isPending={cancelTransfer.isPending}
        error={cancelTransfer.error?.error}
      />

      <DeleteAppDialog appName={appName} open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </section>
  );
}
